import { authenticatedFetch, getAuthHeaders } from './authApi';
import { getEnv } from '../utils/env';

type UnknownRecord = Record<string, unknown>;

export class TrainingContentApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'TrainingContentApiError';
    this.status = status;
  }
}

const toApiBaseUrl = (): string => getEnv('VITE_AI_API_URL', '').replace(/\/$/, '');

const parseJson = async (response: Response): Promise<unknown> => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const extractMessage = (payload: unknown, fallback: string): string => {
  if (payload && typeof payload === 'object' && 'error' in (payload as UnknownRecord)) {
    const error = (payload as UnknownRecord).error;
    if (typeof error === 'string' && error.trim().length > 0) return error;
  }
  if (payload && typeof payload === 'object' && 'message' in (payload as UnknownRecord)) {
    const message = (payload as UnknownRecord).message;
    if (typeof message === 'string' && message.trim().length > 0) return message;
  }
  return fallback;
};

const extractData = (payload: unknown): unknown => {
  if (payload && typeof payload === 'object' && 'data' in (payload as UnknownRecord)) {
    return (payload as UnknownRecord).data;
  }
  return payload;
};

const isFailurePayload = (payload: unknown): boolean => (
  Boolean(
    payload
    && typeof payload === 'object'
    && 'ok' in (payload as UnknownRecord)
    && (payload as UnknownRecord).ok !== true
  )
);

const trainingContentRequest = async (path: string, options: RequestInit = {}): Promise<unknown> => {
  const baseUrl = toApiBaseUrl();
  if (!baseUrl) {
    throw new Error('VITE_AI_API_URL is not configured');
  }

  const response = await authenticatedFetch(`${baseUrl}${path}`, {
    ...options,
    credentials: 'include',
    headers: getAuthHeaders(options.headers || {}),
  });

  const payload = await parseJson(response);
  if (!response.ok || isFailurePayload(payload)) {
    throw new TrainingContentApiError(
      extractMessage(payload, `Training content API request failed (${response.status})`),
      response.status
    );
  }

  return extractData(payload);
};

const trainingContentMutation = async (
  method: 'POST' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown
): Promise<unknown> => trainingContentRequest(path, {
  method,
  headers: { 'X-Exercise-Content-Version': '3' },
  body: body === undefined ? undefined : JSON.stringify(body),
});

export const listTrainingSessionsApi = async (params?: {
  limit?: number;
  offset?: number;
  search?: string;
  age_group?: string;
  level?: string;
  security_scope_id?: string;
  summary?: boolean;
}): Promise<unknown[]> => {
  const query = new URLSearchParams();
  if (typeof params?.limit === 'number') query.set('limit', String(Math.max(1, Math.min(100, params.limit))));
  if (typeof params?.offset === 'number') query.set('offset', String(params.offset));
  if (params?.search) query.set('search', params.search);
  if (params?.age_group) query.set('age_group', params.age_group);
  if (params?.level) query.set('level', params.level);
  if (params?.security_scope_id) query.set('security_scope_id', params.security_scope_id);
  if (params?.summary) query.set('summary', 'true');
  const qs = query.toString();
  const payload = await trainingContentRequest(`/api/v1/training-sessions${qs ? `?${qs}` : ''}`, { method: 'GET' });
  return Array.isArray(payload) ? payload : [];
};

export const listTrainingExercisesApi = async (params?: {
  limit?: number;
  offset?: number;
  search?: string;
  category?: string;
  tag?: string;
  age_group?: string;
  level?: string;
  security_scope_id?: string;
}): Promise<unknown[]> => {
  const query = new URLSearchParams();
  if (typeof params?.limit === 'number') query.set('limit', String(Math.max(1, Math.min(100, params.limit))));
  if (typeof params?.offset === 'number') query.set('offset', String(Math.max(0, params.offset)));
  if (params?.search) query.set('search', params.search);
  if (params?.category) query.set('category', params.category);
  if (params?.tag) query.set('tag', params.tag);
  if (params?.age_group) query.set('age_group', params.age_group);
  if (params?.level) query.set('level', params.level);
  if (params?.security_scope_id) query.set('security_scope_id', params.security_scope_id);
  const qs = query.toString();
  const payload = await trainingContentRequest(`/api/v1/training-exercises${qs ? `?${qs}` : ''}`, { method: 'GET' });
  return Array.isArray(payload) ? payload : [];
};

export const listTrainingPlansApi = async (params?: {
  limit?: number;
  offset?: number;
  search?: string;
  age_group?: string;
  level?: string;
  security_scope_id?: string;
  summary?: boolean;
}): Promise<unknown[]> => {
  const query = new URLSearchParams();
  if (typeof params?.limit === 'number') query.set('limit', String(Math.max(1, Math.min(100, params.limit))));
  if (typeof params?.offset === 'number') query.set('offset', String(params.offset));
  if (params?.search) query.set('search', params.search);
  if (params?.age_group) query.set('age_group', params.age_group);
  if (params?.level) query.set('level', params.level);
  if (params?.security_scope_id) query.set('security_scope_id', params.security_scope_id);
  if (params?.summary) query.set('summary', 'true');
  const qs = query.toString();
  const payload = await trainingContentRequest(`/api/v1/training-plans${qs ? `?${qs}` : ''}`, { method: 'GET' });
  return Array.isArray(payload) ? payload : [];
};

export const getTrainingExerciseDetailApi = async (identifier: string): Promise<unknown | null> => {
  const payload = await trainingContentRequest(`/api/v1/training-exercises/${encodeURIComponent(identifier)}`, { method: 'GET' });
  return payload ?? null;
};

export const createTrainingExerciseApi = async (payload: {
  drill_id?: string | null;
  title: string;
  duration?: number | null;
  categories?: string[];
  tags?: string[];
  introduction?: string | null;
  actors?: unknown[];
  timeline?: unknown[];
  notes?: string[];
  security_level?: string | null;
  security_scope_id?: string | null;
}): Promise<unknown> => trainingContentMutation('POST', '/api/v1/training-exercises', payload);

export const updateTrainingExerciseApi = async (identifier: string, payload: Record<string, unknown>): Promise<unknown> => (
  trainingContentMutation('PATCH', `/api/v1/training-exercises/${encodeURIComponent(identifier)}`, payload)
);

export const deleteTrainingExerciseApi = async (identifier: string): Promise<unknown> => (
  trainingContentMutation('DELETE', `/api/v1/training-exercises/${encodeURIComponent(identifier)}`)
);

export const getTrainingSessionDetailApi = async (identifier: string): Promise<unknown | null> => {
  const payload = await trainingContentRequest(`/api/v1/training-sessions/${encodeURIComponent(identifier)}`, { method: 'GET' });
  return payload ?? null;
};

export const createTrainingSessionApi = async (payload: {
  name: string;
  description?: string | null;
  team_name?: string | null;
  age_group?: string | null;
  level?: string | null;
  security_level?: string | null;
  security_scope_id?: string | null;
  content: unknown;
}): Promise<unknown> => trainingContentMutation('POST', '/api/v1/training-sessions', payload);

export const updateTrainingSessionApi = async (identifier: string, payload: Record<string, unknown>): Promise<unknown> => (
  trainingContentMutation('PATCH', `/api/v1/training-sessions/${encodeURIComponent(identifier)}`, payload)
);

export const deleteTrainingSessionApi = async (identifier: string): Promise<unknown> => (
  trainingContentMutation('DELETE', `/api/v1/training-sessions/${encodeURIComponent(identifier)}`)
);

export const getTrainingPlanDetailApi = async (identifier: string): Promise<unknown | null> => {
  const payload = await trainingContentRequest(`/api/v1/training-plans/${encodeURIComponent(identifier)}`, { method: 'GET' });
  return payload ?? null;
};

export const createTrainingPlanApi = async (payload: {
  name: string;
  team_name?: string | null;
  age_group?: string | null;
  level?: string | null;
  trainings_per_week: number;
  number_of_weeks: number;
  start_date?: string | null;
  notes?: string | null;
  weeks: unknown[];
  security_level?: string | null;
  security_scope_id?: string | null;
}): Promise<unknown> => trainingContentMutation('POST', '/api/v1/training-plans', payload);

export const updateTrainingPlanApi = async (identifier: string, payload: Record<string, unknown>): Promise<unknown> => (
  trainingContentMutation('PATCH', `/api/v1/training-plans/${encodeURIComponent(identifier)}`, payload)
);

export const deleteTrainingPlanApi = async (identifier: string): Promise<unknown> => (
  trainingContentMutation('DELETE', `/api/v1/training-plans/${encodeURIComponent(identifier)}`)
);
